'use client';
import React, { useEffect, useState } from 'react';
import { TestimonialsProps } from './types';
import Badge from '@/components/badge/Badge';
import { BaseImage } from '@/components/baseImage/BaseImage';
import { Button } from '@/components/button/Button';
import './styles.css';
import { Headline } from '@/components/headline/Headline';
import {
  goToSlide,
  nextSlide,
  prevSlide,
  toggleReadMore,
  updateView,
} from './utils';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';

const Testimonials: React.FC<TestimonialsProps> = ({
  items,
  readMore = false,
  sectionId,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [fadeEffect, setFadeEffect] = useState('opacity-visible');
  const [imageFadeEffect, setImageFadeEffect] = useState('opacity-visible');
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    updateView(setIsMobileView);
    window.addEventListener('resize', () => updateView(setIsMobileView));
    return () =>
      window.removeEventListener('resize', () => updateView(setIsMobileView));
  }, []);

  const renderBadges = () => (
    <div className="badges flex justify-start">
      {items[currentIndex]?.badges?.map((badge, index) => (
        <Badge
          key={index}
          variant={badge.variant}
          title={badge.title}
          size={badge.size}
          uppercase={badge.uppercase}
          classNameBadge="mr-2.5"
        />
      ))}
    </div>
  );

  const renderContent = () =>
    expanded ? (
      <>
        {items[currentIndex]?.moreHeadline && (
          <Headline {...items[currentIndex]?.headline}>
            {items[currentIndex]?.moreHeadline.title}
          </Headline>
        )}
        <div
          className={
            'scrollbar-hide overflow-y-scroll text-sm text-brand-700 lg:text-base'
          }
        >
          {items[currentIndex].moreDescription && (
            <SerializeDescription
              description={items[currentIndex]?.moreDescription}
            />
          )}
          {/*<p*/}
          {/*  className={*/}
          {/*    'scrollbar-hide overflow-y-scroll text-sm text-brand-700 lg:text-base'*/}
          {/*  }*/}
          {/*>*/}
          {/*  {items[currentIndex]?.moreDescription}*/}
          {/*</p>*/}
        </div>
      </>
    ) : (
      <div>
        {renderBadges()}
        {items[currentIndex]?.headline && (
          <Headline {...items[currentIndex]?.headline}>
            {items[currentIndex]?.headline.title}
          </Headline>
        )}
        <div
          className={
            'scrollbar-hide overflow-y-scroll text-sm text-brand-700 lg:text-base'
          }
        >
          {items[currentIndex].description && (
            <SerializeDescription
              description={items[currentIndex].description}
            />
          )}
        </div>
      </div>
    );

  return (
    <div
      id={sectionId}
      className="testimonials-slider container mx-auto px-4 py-4 sm:px-0 lg:px-0"
      data-component-id="CO29"
    >
      <div
        className={`slider-content grid h-fit grid-cols-4 gap-8 rounded-2xl border bg-light-grey p-4 lg:h-fit lg:grid-cols-12 lg:px-12 ${
          items.length > 1 ? 'lg:pt-12' : 'lg:py-12'
        }`}
      >
        {' '}
        {/*image disappear on mobile view*/}
        {!(isMobileView && expanded) && (
          <div className="left-image align-center col-span-full flex h-fit flex-col-reverse md:flex-col lg:col-span-4">
            <div
              className={`image-container transition-opacity ${imageFadeEffect}`}
            >
              <BaseImage
                {...items[currentIndex].image}
                classNameImage={'w-full h-[350px] rounded-xl object-cover'}
                sizes="(max-width: 1024px) 100vw, 33vw"
              />
            </div>
            {items.length > 1 && (
              <div className="slider-controls flex items-center justify-center space-x-4">
                <Button
                  variant="tertiary"
                  leftIcon={{ name: 'arrow-left', size: '14px' }}
                  onClick={() =>
                    prevSlide(
                      currentIndex,
                      items.length,
                      setCurrentIndex,
                      setExpanded,
                      setImageFadeEffect,
                      setFadeEffect,
                    )
                  }
                  aria-label="Previous Slide"
                  disabled={currentIndex === 0}
                  className={`text-brand-700 ${currentIndex === 0 ? 'cursor-not-allowed opacity-50' : ''} `}
                />
                <div className="flex space-x-2">
                  {items.map((_, index) => (
                    <span
                      key={index}
                      className={`${
                        currentIndex === index
                          ? 'font-bold text-black'
                          : 'text-gray-400'
                      } cursor-pointer`}
                      onClick={() =>
                        goToSlide(
                          index,
                          setCurrentIndex,
                          setExpanded,
                          setImageFadeEffect,
                          setFadeEffect,
                        )
                      }
                    >
                      {index + 1}
                    </span>
                  ))}
                </div>
                <Button
                  variant="tertiary"
                  rightIcon={{ name: 'arrow-right', size: '14px' }}
                  onClick={() =>
                    nextSlide(
                      currentIndex,
                      items.length,
                      setCurrentIndex,
                      setExpanded,
                      setImageFadeEffect,
                      setFadeEffect,
                    )
                  }
                  aria-label="Next Slide"
                  disabled={currentIndex === items.length - 1}
                  className={
                    currentIndex === items.length - 1
                      ? 'cursor-not-allowed opacity-50'
                      : ''
                  }
                />
              </div>
            )}
          </div>
        )}
        <div
          className={`right-content col-span-full flex h-[350px] flex-col justify-between p-4 lg:col-span-8 lg:p-6 ${expanded ? 'bg-white' : 'bg-light-grey'} rounded-xl`}
        >
          <div className={`top-content transition-opacity ${fadeEffect}`}>
            {renderContent()}
          </div>
          <div
            className={`bottom-content mt-5 flex justify-start transition-opacity lg:justify-end ${fadeEffect}`}
          >
            {readMore && (
              <Button
                variant="secondary"
                className="read-more-button border-brand-50000"
                onClick={() =>
                  toggleReadMore(
                    setFadeEffect,
                    setExpanded,
                    isMobileView,
                    setImageFadeEffect,
                  )
                }
                aria-label={expanded ? '' : 'Read More'}
                leftIcon={
                  expanded
                    ? { name: 'arrow-down-right', size: '20px' }
                    : { name: 'arrow-up-left', size: '20px' }
                }
                name={expanded ? '' : 'Mehr'}
                classNameSpan="gap-2"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;
